/**
 * Which panels a project page draws, and which one is open.
 *
 * React-free on purpose, for the same reason `account/tabs.ts` is: the strip and
 * the panel dispatch are two separate pieces of JSX asking the same question,
 * and they only stay honest if both call the same function. Everything here is a
 * pure function over strings, so `tabs.test.ts` beside it needs no DOM.
 *
 * ── THE REGISTRY IS THIS BUILD'S VOCABULARY, NOT THE SERVER'S ──────────────
 *
 * `PORTAL_PROJECT_TABS` in `@wa/core` is the full vocabulary both sides share.
 * The registry below is the subset this build can actually paint, and the two
 * are allowed to differ in both directions: a newer API may grant a tab this
 * bundle has never heard of, and this bundle knows tabs a given client was not
 * granted. Neither may be drawn, which is what `visibleProjectTabs` is for.
 *
 * `files` is in the shared vocabulary and deliberately NOT here. There is no
 * `portal.files(...)` call and no panel, and the server does not grant it today
 * either - but "the server does not grant it" is a fact about today's API, not a
 * guarantee. Leaving it out of this list is the guarantee: an unknown tab cannot
 * be drawn, so the day the grant appears the worst case is a missing button
 * rather than a tab that opens onto nothing.
 */
import type { PortalProjectTab } from "@wa/core/portal-project-tabs";

/**
 * The glyph a tab wears, named rather than imported.
 *
 * A `lucide-react` import here would drag React into a file whose whole point is
 * that it can be tested without it. `ProjectPage` owns the name -> component map
 * and types it as a total `Record`, so adding an entry below without a glyph is
 * a build failure rather than a blank space in the strip.
 */
export type ProjectTabIcon =
  | "overview"
  | "milestones"
  | "tasks"
  | "invoices"
  | "estimates"
  | "contracts"
  | "tickets";

export interface ProjectTabSpec {
  id: PortalProjectTab;
  icon: ProjectTabIcon;
  /**
   * The strip label's catalogue key, written out rather than built from the id.
   *
   * Ids contain hyphens (`linked-invoices`), which a key segment may hold. What
   * it may never hold is a dot: `t()` splits on it, so a dotted segment would
   * walk into a nested object that does not exist and print the key at the
   * client. The test pins both halves of that rule.
   */
  labelKey: string;
}

/**
 * Every tab this build can paint, in strip order.
 *
 * The order mirrors `PORTAL_PROJECT_TABS` (minus `files`), and the test asserts
 * it: the CRM's tab editor lists the grants in that order, so an operator who
 * arranged them there should recognise the strip the client gets.
 */
export const PROJECT_TAB_SPECS: readonly ProjectTabSpec[] = [
  { id: "overview", icon: "overview", labelKey: "portal.projectTab.overview" },
  { id: "milestones", icon: "milestones", labelKey: "portal.projectTab.milestones" },
  { id: "tasks", icon: "tasks", labelKey: "portal.projectTab.tasks" },
  { id: "linked-invoices", icon: "invoices", labelKey: "portal.projectTab.linked-invoices" },
  { id: "linked-estimates", icon: "estimates", labelKey: "portal.projectTab.linked-estimates" },
  { id: "linked-contracts", icon: "contracts", labelKey: "portal.projectTab.linked-contracts" },
  { id: "linked-tickets", icon: "tickets", labelKey: "portal.projectTab.linked-tickets" },
];

/** Which tabs this page draws: the INTERSECTION of what the server granted
 *  (detail.tabs) and what this build knows how to render. Not the union and
 *  not the server's list alone - a tab name from a newer API would render an
 *  empty panel, and a tab this build knows that the server did not grant must
 *  not appear at all. The server is the authority on permission; this file is
 *  the authority on what exists. */
export function visibleProjectTabs(served: readonly string[]): PortalProjectTab[] {
  const granted = new Set<string>(served);
  // Filtering the registry rather than mapping the server's list does two jobs
  // at once: the strip order is this build's whatever order the payload arrived
  // in, and a repeated name collapses instead of drawing the tab twice.
  return PROJECT_TAB_SPECS.filter((spec) => granted.has(spec.id)).map((spec) => spec.id);
}

/** An unknown or ungranted ?sub= falls back to the first open tab, exactly as
 *  account/tabs.ts falls back for the top-level strip. Hiding a button is not
 *  closing a door. */
export function resolveSubTab(
  requested: string | null,
  open: readonly PortalProjectTab[],
): PortalProjectTab {
  const allowed: readonly string[] = open;
  if (requested && allowed.includes(requested)) return requested as PortalProjectTab;
  // `open` is empty only while the detail is in flight, or if a server ever
  // answered with nothing this build recognises. `overview` is the honest answer
  // to both: it is the one tab `PORTAL_PROJECT_TAB_FLOOR` keeps permanently
  // granted, so it is the tab the next payload is most likely to open on.
  return open[0] ?? "overview";
}

/** The spec for a tab, for the strip. Undefined for a tab this build cannot
 *  paint, which `visibleProjectTabs` has already filtered out. */
export function projectTabSpec(id: PortalProjectTab): ProjectTabSpec | undefined {
  return PROJECT_TAB_SPECS.find((spec) => spec.id === id);
}
